/**
 * Dictation. Press to start, press again to stop — not hold-to-talk, which
 * does not work with a keyboard.
 *
 * Live where the deployment can (`capabilities.voice`): the microphone goes
 * to `/listen` (Deepgram Nova-3) and the words come back as they are said,
 * shown in the draft as `heard` while they firm up. Stopping waits a moment
 * for the last phrase, then hands the lot over.
 *
 * Otherwise, and after a live socket that never got going, the old way: a
 * recording sent whole to `POST /transcribe` (Whisper) when it stops.
 *
 * `supported` is false where the browser can do neither; the composer hides
 * the microphone then.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribe } from '../api';
import { canListen, listen, type Listening } from '../audio/listen';
import { hear, heard as heardOf, NO_DICTATION, type Dictation } from '../audio/dictationText';

export type DictationState = 'idle' | 'listening' | 'transcribing';

/** Set once a live socket fails to start, so the next press records instead. */
let liveBroken = false;

export function useDictation(onText: (text: string) => void, { live = false }: { live?: boolean } = {}) {
  const canRecord =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
  const supported = canRecord || (live && canListen());

  const [state, setState] = useState<DictationState>('idle');
  const [failed, setFailed] = useState<string | null>(null);
  const [heard, setHeard] = useState('');
  const recorder = useRef<MediaRecorder | null>(null);
  const socket = useRef<Listening | null>(null);
  const deliver = useRef(onText);
  deliver.current = onText;

  // Leaving the page mid-dictation must release the microphone.
  useEffect(
    () => () => {
      const r = recorder.current;
      recorder.current = null;
      if (r && r.state !== 'inactive') r.stop();
      r?.stream.getTracks().forEach((t) => t.stop());
      socket.current?.close();
      socket.current = null;
    },
    []
  );

  const startLive = useCallback(async (): Promise<boolean> => {
    let words: Dictation = NO_DICTATION;
    let ready = false;
    let said: string | null = null;
    try {
      const s = await listen('dictate', {
        onEvent: (e) => {
          if (e.type === 'ready') ready = true;
          else if (e.type === 'text') {
            words = hear(words, e);
            setHeard(heardOf(words));
          } else if (e.type === 'error') said = e.message;
        },
        onClosed: () => {
          if (socket.current !== s) return;
          socket.current = null;
          const text = heardOf(words).trim();
          setHeard('');
          setState('idle');
          if (text) deliver.current(text);
          if (said) setFailed(said);
          else if (!ready) {
            // Never got going (a proxy, a blocked socket): record next time.
            liveBroken = true;
            setFailed('That did not come through. Try again.');
          }
        }
      });
      socket.current = s;
      setState('listening');
      return true;
    } catch {
      // No microphone, or no permission for it.
      setFailed('The microphone is not available.');
      return false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setFailed('The microphone is not available.');
      return;
    }

    const r = new MediaRecorder(stream);
    const chunks: Blob[] = [];
    r.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    r.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      // Unmounted: the recording is dropped rather than sent.
      if (recorder.current !== r) return;
      recorder.current = null;
      if (!chunks.length) {
        setState('idle');
        return;
      }
      setState('transcribing');
      const audio = new Blob(chunks, { type: r.mimeType || 'audio/webm' });
      transcribe(audio)
        .then(({ text }) => {
          if (text.trim()) deliver.current(text.trim());
        })
        .catch(() => setFailed('That did not come through. Try again.'))
        .finally(() => setState('idle'));
    };

    recorder.current = r;
    r.start();
    setState('listening');
  }, []);

  const start = useCallback(async () => {
    if (!supported || recorder.current || socket.current) return;
    setFailed(null);
    if (live && !liveBroken && canListen()) {
      await startLive();
      return;
    }
    if (canRecord) await startRecording();
  }, [supported, live, canRecord, startLive, startRecording]);

  const stop = useCallback(() => {
    if (socket.current) {
      // The last phrase is still on its way: wait for it, then hand over.
      setState('transcribing');
      socket.current.finish();
      return;
    }
    const r = recorder.current;
    if (r && r.state !== 'inactive') r.stop();
  }, []);

  const toggle = useCallback(() => {
    if (state === 'listening') stop();
    else if (state === 'idle') void start();
  }, [state, start, stop]);

  return { supported, state, heard, failed, toggle, clearFailed: () => setFailed(null) };
}
